import { useSyncExternalStore } from 'react';

/**
 * The Brain's view state that the prototype keeps in app state across tab
 * switches and Brain visits (`brBranch`, `brNode`, `brTab`): the selected
 * branch chip, the selected node and the last tab. Module-level so it survives
 * BrainTreeTab unmounting when another tab is open, and leaving the Brain.
 *
 * Import-safe (no work at module load). `lastBrainTab()` is for the Wiki nav's
 * AI Brain row (prototype: re-entering the Brain restores the last tab).
 */

const INITIAL = Object.freeze({ branchId: 'wiki', nodeId: null, lastTab: 'tree' });
let state = INITIAL;
const listeners = new Set();

function setState(patch) {
    const next = { ...state, ...patch };
    if (next.branchId === state.branchId && next.nodeId === state.nodeId && next.lastTab === state.lastTab) return;
    state = next;
    listeners.forEach((listener) => listener());
}

export const brainTreeState = {
    getState: () => state,
    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },
    /** Branch chip click: switch branch and clear the selection (prototype). */
    selectBranch(branchId) {
        setState({ branchId: branchId || 'wiki', nodeId: null });
    },
    /** Select a node (null clears). */
    selectNode(nodeId) {
        setState({ nodeId: nodeId || null });
    },
    /** Select a branch AND a node in it (after creating a branch). */
    selectBranchNode(branchId, nodeId) {
        setState({ branchId: branchId || 'wiki', nodeId: nodeId || null });
    },
    rememberTab(tab) {
        if (tab) setState({ lastTab: tab });
    },
    /** Tests only. */
    reset() {
        state = INITIAL;
        listeners.forEach((listener) => listener());
    },
};

/** The last Brain tab shown ('tree' | 'review' | 'insights' | 'settings'). */
export function lastBrainTab() {
    return state.lastTab;
}

const identity = (s) => s;

/** `useBrainTreeState(s => s.nodeId)` — the selector must return a stable value. */
export function useBrainTreeState(selector = identity) {
    return useSyncExternalStore(
        brainTreeState.subscribe,
        () => selector(state),
        () => selector(state),
    );
}
